/**
 * Komunikimi — kanalet, njoftimet dhe mesazhet direkte.
 *
 * E njëjta shërbesë që ushqen Nexuma Mobile (`/api/chat/*`,
 * `backend/routes/chatRoutes.js`): një bisedë e nisur në telefon vazhdon këtu,
 * me të njëjtat të palexuara. Web-i s'e kopjon logjikën — thjesht i jep asaj
 * hapësirën e një ekrani të madh: lista majtas, biseda djathtas, të dyja të
 * dukshme njëherësh.
 *
 * Lista freskohet çdo 8 sekonda vetëm kur s'ka bisedë të hapur ose kur skeda
 * është e dukshme; brenda bisedës këtë punë e bën polling-u i `ChatRoom`.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Stack, Typography, TextField, InputAdornment, IconButton, Avatar,
  Badge, Chip, Tabs, Tab, Divider, Tooltip, Alert, Skeleton, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, List, ListItemButton,
  ListItemAvatar, ListItemText, CircularProgress,
} from '@mui/material';
import SearchRoundedIcon        from '@mui/icons-material/SearchRounded';
import AddRoundedIcon           from '@mui/icons-material/AddRounded';
import TagRoundedIcon           from '@mui/icons-material/TagRounded';
import CampaignRoundedIcon      from '@mui/icons-material/CampaignRounded';
import PushPinRoundedIcon       from '@mui/icons-material/PushPinRounded';
import ForumRoundedIcon         from '@mui/icons-material/ForumRounded';
import RefreshRoundedIcon       from '@mui/icons-material/RefreshRounded';
import ChatBubbleOutlineRounded from '@mui/icons-material/ChatBubbleOutlineRounded';
import GroupsRounded            from '@mui/icons-material/GroupsRounded';
import CheckRoundedIcon         from '@mui/icons-material/CheckRounded';
import PageHeader from '../components/common/PageHeader';
import ChatRoom from '../components/chat/ChatRoom';
import usePermissions from '../hooks/usePermissions';
import PERMISSIONS from '../config/permissions/permissionsConfig';
import {
  chatOverview, chatPeople, chatOpenDm, chatCreateChannel, chatCreateGroup,
} from '../services/chatApi';

const REFRESH_MS = 8000;

const T = {
  brand: '#3D8FA3', ink: '#0F172A', ink700: '#334155', ink500: '#64748B',
  ink400: '#94A3B8', line: '#E2E8F0', surface: '#F8FAFC', white: '#FFFFFF',
};

const initial = (s) => String(s || '?').trim().charAt(0).toLocaleUpperCase('sq');

const AVATAR_COLORS = ['#3D8FA3', '#6B5AA6', '#3D8F6F', '#B4713D', '#8A5A72', '#4F6BA6'];
const avatarColor = (name) => {
  const s = String(name || '');
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) % 997;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
};

/** Ora për sot, "Dje", pastaj data — një rresht liste s'ka vend për më shumë. */
export function timeLabel(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const p = (n) => String(n).padStart(2, '0');
  if (d.toDateString() === now.toDateString()) return `${p(d.getHours())}:${p(d.getMinutes())}`;
  if (d.toDateString() === new Date(now.getTime() - 86400000).toDateString()) return 'Dje';
  return `${d.getDate()}.${p(d.getMonth() + 1)}`;
}

/** Rreshti i fundit: "Ti: …" kur e ke shkruar ti, "Emri: …" te kanalet. */
export function lastLine(c) {
  if (!c.last) return c.kind === 'dm' ? 'Ende asnjë mesazh' : (c.purpose || 'Ende asnjë mesazh');
  const who = c.last.mine ? 'Ti' : (c.last.author || '');
  const body = String(c.last.body || '').replace(/\s+/g, ' ').trim();
  return who ? `${who}: ${body}` : body;
}

const matches = (c, needle) => !needle
  || String(c.name || '').toLowerCase().includes(needle)
  || String(c.last?.body || '').toLowerCase().includes(needle);

export default function CommunicationPage() {
  const { can } = usePermissions();
  // Serveri e lejon krijimin e kanaleve vetëm për admin/superadmin/menaxher
  // (chatRoutes.js). Butoni fshihet po me atë kufi, që të mos ofrojë 403.
  const canCreateChannel = can(PERMISSIONS.COMMUNICATION_MANAGE)
    || can(PERMISSIONS.COMMUNICATION_CREATE_COMPANY);

  const [data,    setData]    = useState({ channels: [], dms: [], unread: 0 });
  const [open,    setOpen]    = useState(null);     // biseda e hapur
  const [tab,     setTab]     = useState('channels');
  const [q,       setQ]       = useState('');
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');

  const [people,     setPeople]     = useState(null); // null = ende pa u kërkuar
  const [dmOpen,     setDmOpen]     = useState(false);
  const [dmBusy,     setDmBusy]     = useState(false);
  const [peopleQ,    setPeopleQ]    = useState('');
  const [channelOpen, setChannelOpen] = useState(false);
  const [newChannel, setNewChannel] = useState({ name: '', purpose: '' });
  /**
   * Grupi që po ndërtohet.
   *
   * Ndahet nga kanali sepse rregullat janë të ndryshme: kanali kërkon privilegj
   * dhe i hapet gjithë firmës, grupi e hap kushdo dhe kërkon të paktën një koleg.
   */
  const [groupOpen, setGroupOpen] = useState(false);
  const [groupBusy, setGroupBusy] = useState(false);
  const [newGroup,  setNewGroup]  = useState({ name: '', ids: [] });
  const [channelBusy, setChannelBusy] = useState(false);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      setData(await chatOverview());
      setError('');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Kur je brenda një bisede, ChatRoom-i e bën vetë freskimin — kjo listë do të
  // pyeste serverin dy herë për të njëjtën gjë.
  useEffect(() => {
    const id = setInterval(() => { if (!document.hidden) load({ silent: true }); }, REFRESH_MS);
    return () => clearInterval(id);
  }, [load]);

  const needle = q.trim().toLowerCase();
  const filtered = useCallback((list) => list.filter((c) => matches(c, needle)), [needle]);

  // "Njoftimet" janë kanalet me zë megafoni — jo një listë tjetër e shpikur.
  const notices = useMemo(() => filtered(data.channels.filter((c) => c.icon === 'megaphone')), [data.channels, filtered]);
  const channels = useMemo(() => filtered(data.channels.filter((c) => c.icon !== 'megaphone')), [data.channels, filtered]);
  const dms      = useMemo(() => filtered(data.dms), [data.dms, filtered]);

  const unreadDms      = data.dms.reduce((s, c) => s + (c.unread || 0), 0);
  const unreadChannels = data.channels.reduce((s, c) => s + (c.unread || 0), 0);

  const list = tab === 'dms' ? dms : tab === 'notices' ? notices : channels;

  const openConversation = (c) => {
    setOpen(c);
    // Rreshti hapet i lexuar: ChatRoom-i e shënon te serveri, kjo e heq numrin
    // menjëherë që syri të mos shohë një kundërthënie.
    setData((prev) => ({
      ...prev,
      channels: prev.channels.map((x) => (x.id === c.id ? { ...x, unread: 0 } : x)),
      dms:      prev.dms.map((x) => (x.id === c.id ? { ...x, unread: 0 } : x)),
    }));
  };

  const openPeople = async () => {
    setDmOpen(true);
    setPeopleQ('');
    if (people) return;
    try { setPeople(await chatPeople()); } catch (e) { setError(e.message); setPeople([]); }
  };

  const startDm = async (person) => {
    setDmBusy(true);
    try {
      const { id } = await chatOpenDm(person.id);
      setDmOpen(false);
      setTab('dms');
      await load({ silent: true });
      openConversation({ id, kind: 'dm', name: person.name });
    } catch (e) {
      setError(e.message);
    } finally {
      setDmBusy(false);
    }
  };

  const createChannel = async () => {
    const name = newChannel.name.trim();
    if (!name) return;
    setChannelBusy(true);
    try {
      const c = await chatCreateChannel({ name, purpose: newChannel.purpose.trim() });
      setChannelOpen(false);
      setNewChannel({ name: '', purpose: '' });
      await load({ silent: true });
      setTab('channels');
      openConversation({ id: c.id, kind: 'channel', name: c.name });
    } catch (e) {
      setError(e.message);
    } finally {
      setChannelBusy(false);
    }
  };

  const toggleGroupMember = (id) => setNewGroup((g) => ({
    ...g,
    ids: g.ids.includes(id) ? g.ids.filter((x) => x !== id) : [...g.ids, id],
  }));

  const createGroup = async () => {
    const name = newGroup.name.trim();
    if (!name || newGroup.ids.length === 0) return;
    setGroupBusy(true);
    try {
      const c = await chatCreateGroup({ name, members: newGroup.ids });
      setGroupOpen(false);
      setNewGroup({ name: '', ids: [] });
      await load({ silent: true });
      setTab('channels');
      openConversation({ id: c.id, kind: 'channel', name: c.name });
    } catch (e) {
      setError(e.message);
    } finally {
      setGroupBusy(false);
    }
  };

  const visiblePeople = (people || []).filter((p) =>
    !peopleQ.trim() || p.name.toLowerCase().includes(peopleQ.trim().toLowerCase()));

  return (
    <Box>
      <PageHeader title="Komunikimi" />

      <Paper elevation={0} sx={{
        border: `1px solid ${T.line}`, borderRadius: 2, overflow: 'hidden',
        display: 'flex', height: 'calc(100vh - 200px)', minHeight: 460,
      }}>
        {/* ── Lista e bisedave ── */}
        <Box sx={{
          width: { xs: '100%', md: 340 }, flexShrink: 0,
          display: { xs: open ? 'none' : 'flex', md: 'flex' },
          flexDirection: 'column', borderRight: { md: `1px solid ${T.line}` }, bgcolor: T.white,
        }}>
          <Box sx={{ px: 2, pt: 1.75, pb: 1.25 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.95rem', fontWeight: 800, color: T.ink, lineHeight: 1.2 }}>
                  Bisedat
                </Typography>
                <Typography sx={{ fontSize: '0.72rem', color: T.ink500 }}>
                  {data.unread > 0 ? `${data.unread} të palexuara` : 'Ekipi yt, gjithmonë i lidhur'}
                </Typography>
              </Box>
              <Tooltip title="Rifresko">
                <span>
                  <IconButton size="small" onClick={() => load({ silent: true })} disabled={loading}
                    sx={{ border: `1px solid ${T.line}`, borderRadius: 1.5, width: 32, height: 32 }}>
                    <RefreshRoundedIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Nis një bisedë">
                <IconButton size="small" onClick={openPeople}
                  sx={{ width: 32, height: 32, bgcolor: T.brand, color: '#FFF', '&:hover': { bgcolor: '#33788A' } }}>
                  <AddRoundedIcon sx={{ fontSize: 19 }} />
                </IconButton>
              </Tooltip>
            </Stack>

            <TextField
              fullWidth size="small" placeholder="Kërko bisedë ose mesazh…"
              value={q} onChange={(e) => setQ(e.target.value)}
              sx={{
                mt: 1.25,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px', bgcolor: T.surface, fontSize: '0.82rem',
                  '& fieldset': { borderColor: T.line },
                  '&.Mui-focused fieldset': { borderColor: T.brand, borderWidth: 1 },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon sx={{ fontSize: 17, color: T.ink400 }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Tabs
            value={tab} onChange={(_, v) => setTab(v)} variant="fullWidth"
            sx={{
              minHeight: 38, borderBottom: `1px solid ${T.line}`,
              '& .MuiTab-root': { minHeight: 38, fontSize: '0.75rem', fontWeight: 700, textTransform: 'none' },
              '& .Mui-selected': { color: `${T.brand} !important` },
              '& .MuiTabs-indicator': { bgcolor: T.brand },
            }}
          >
            <Tab value="channels" label={<Badge badgeContent={unreadChannels} color="error" max={99}
              sx={{ '& .MuiBadge-badge': { right: -10, top: 2, fontSize: '0.6rem', height: 15, minWidth: 15 } }}>Kanalet</Badge>} />
            <Tab value="dms" label={<Badge badgeContent={unreadDms} color="error" max={99}
              sx={{ '& .MuiBadge-badge': { right: -10, top: 2, fontSize: '0.6rem', height: 15, minWidth: 15 } }}>Mesazhet</Badge>} />
            <Tab value="notices" label="Njoftimet" />
          </Tabs>

          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            {error && (
              <Alert severity="error" sx={{ m: 1.5, fontSize: '0.78rem', borderRadius: 2 }}
                action={<Button size="small" onClick={() => load()} sx={{ textTransform: 'none', fontWeight: 700 }}>Provo</Button>}>
                {error}
              </Alert>
            )}

            {loading ? (
              <Stack spacing={1.5} sx={{ p: 2 }}>
                {[0, 1, 2, 3].map((i) => (
                  <Stack key={i} direction="row" spacing={1.25} alignItems="center">
                    <Skeleton variant="circular" width={38} height={38} />
                    <Box sx={{ flex: 1 }}>
                      <Skeleton width="55%" height={14} />
                      <Skeleton width="80%" height={12} />
                    </Box>
                  </Stack>
                ))}
              </Stack>
            ) : list.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <ChatBubbleOutlineRounded sx={{ fontSize: 32, color: T.line }} />
                <Typography sx={{ mt: 1, fontSize: '0.8rem', color: T.ink400 }}>
                  {needle ? 'Asnjë bisedë nuk përputhet me kërkimin.'
                    : tab === 'dms' ? 'Ende s’ke biseda. Shtyp + për të nisur një.'
                      : tab === 'notices' ? 'S’ka kanale njoftimesh.'
                        : 'Ende s’je pjesë e ndonjë kanali.'}
                </Typography>
              </Box>
            ) : list.map((c) => {
              const active = open?.id === c.id;
              return (
                <Box
                  key={c.id} onClick={() => openConversation(c)} role="button" tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openConversation(c); } }}
                  sx={{
                    px: 2, py: 1.25, display: 'flex', alignItems: 'center', gap: 1.25, cursor: 'pointer',
                    borderBottom: `1px solid ${T.surface}`,
                    bgcolor: active ? '#E8F4F6' : 'transparent',
                    borderLeft: `3px solid ${active ? T.brand : 'transparent'}`,
                    '&:hover': { bgcolor: active ? '#E8F4F6' : T.surface },
                  }}
                >
                  <Badge
                    overlap="circular" variant="dot" color="success"
                    invisible={!(c.kind === 'dm' && c.peer?.online)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  >
                    <Avatar sx={{
                      width: 38, height: 38, fontSize: '0.85rem', fontWeight: 800,
                      bgcolor: c.kind === 'dm' ? avatarColor(c.name) : c.icon === 'megaphone' ? '#FDF0E6' : '#E8F4F6',
                      color: c.kind === 'dm' ? '#FFFFFF' : c.icon === 'megaphone' ? '#B4713D' : T.brand,
                    }}>
                      {c.kind === 'dm' ? initial(c.name)
                        : c.icon === 'megaphone' ? <CampaignRoundedIcon sx={{ fontSize: 20 }} />
                          : <TagRoundedIcon sx={{ fontSize: 20 }} />}
                    </Avatar>
                  </Badge>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      {c.pinned && <PushPinRoundedIcon sx={{ fontSize: 12, color: T.ink400 }} />}
                      <Typography noWrap sx={{
                        fontSize: '0.84rem', color: T.ink, flex: 1,
                        fontWeight: c.unread ? 800 : 700,
                      }}>
                        {c.kind === 'dm' ? c.name : `#${c.name}`}
                      </Typography>
                      <Typography sx={{ fontSize: '0.67rem', color: T.ink400, flexShrink: 0 }}>
                        {timeLabel(c.last?.at)}
                      </Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                      <Typography noWrap sx={{
                        fontSize: '0.75rem', flex: 1,
                        color: c.unread ? T.ink700 : T.ink500,
                        fontWeight: c.unread ? 700 : 400,
                      }}>
                        {lastLine(c)}
                      </Typography>
                      {c.unread > 0 && (
                        <Chip size="small" label={c.unread > 99 ? '99+' : c.unread} sx={{
                          height: 18, minWidth: 18, fontSize: '0.65rem', fontWeight: 800,
                          bgcolor: T.brand, color: '#FFFFFF', '& .MuiChip-label': { px: 0.75 },
                        }} />
                      )}
                    </Stack>
                  </Box>
                </Box>
              );
            })}
          </Box>

          <Divider />
          <Stack direction="row" sx={{ p: 1.25 }} spacing={1}>
            <Button
              fullWidth size="small" startIcon={<GroupsRounded sx={{ fontSize: 18 }} />}
              onClick={() => { setNewGroup({ name: '', ids: [] }); setGroupOpen(true); }}
              sx={{ textTransform: 'none', fontWeight: 700, color: T.brand, fontSize: '0.8rem' }}
            >
              Grup i ri
            </Button>
            {canCreateChannel && (
              <Button
                fullWidth size="small" startIcon={<TagRoundedIcon sx={{ fontSize: 17 }} />}
                onClick={() => setChannelOpen(true)}
                sx={{ textTransform: 'none', fontWeight: 700, color: T.brand, fontSize: '0.8rem' }}
              >
                Kanal i ri
              </Button>
            )}
          </Stack>
        </Box>

        {/* ── Biseda ── */}
        <Box sx={{
          flex: 1, minWidth: 0,
          display: { xs: open ? 'flex' : 'none', md: 'flex' },
        }}>
          {open ? (
            <ChatRoom
              key={open.id}
              channel={open}
              onBack={() => { setOpen(null); load({ silent: true }); }}
              onActivity={() => load({ silent: true })}
            />
          ) : (
            <Stack alignItems="center" justifyContent="center" spacing={1} sx={{ width: '100%', bgcolor: T.surface }}>
              <ForumRoundedIcon sx={{ fontSize: 46, color: T.line }} />
              <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: T.ink700 }}>
                Zgjidh një bisedë
              </Typography>
              <Typography sx={{ fontSize: '0.8rem', color: T.ink400, textAlign: 'center', maxWidth: 300 }}>
                Kanalet janë të hapura për të gjithë; mesazhet direkte i sheh vetëm ti dhe kolegu.
              </Typography>
            </Stack>
          )}
        </Box>
      </Paper>

      {/* ── Nis një bisedë ── */}
      <Dialog open={dmOpen} onClose={() => setDmOpen(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 2.5 } }}>
        <DialogTitle sx={{ fontWeight: 800, fontSize: '1rem' }}>Nis një bisedë</DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <Box sx={{ p: 1.5 }}>
            <TextField
              autoFocus fullWidth size="small" placeholder="Kërko koleg…"
              value={peopleQ} onChange={(e) => setPeopleQ(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon sx={{ fontSize: 17, color: T.ink400 }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Box sx={{ maxHeight: 340, overflowY: 'auto' }}>
            {people === null ? (
              <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress size={22} /></Stack>
            ) : visiblePeople.length === 0 ? (
              <Typography sx={{ p: 3, textAlign: 'center', fontSize: '0.82rem', color: T.ink400 }}>
                Asnjë koleg për të shfaqur.
              </Typography>
            ) : (
              <List dense disablePadding>
                {visiblePeople.map((p) => (
                  <ListItemButton key={p.id} onClick={() => startDm(p)} disabled={dmBusy}>
                    <ListItemAvatar sx={{ minWidth: 44 }}>
                      <Badge overlap="circular" variant="dot" color="success" invisible={!p.online}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                        <Avatar sx={{ width: 32, height: 32, fontSize: '0.78rem', fontWeight: 800, bgcolor: avatarColor(p.name), color: '#FFF' }}>
                          {initial(p.name)}
                        </Avatar>
                      </Badge>
                    </ListItemAvatar>
                    <ListItemText
                      primary={p.name}
                      secondary={p.online ? 'Online' : p.email}
                      primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 700 }}
                      secondaryTypographyProps={{ fontSize: '0.72rem', color: p.online ? '#16A34A' : T.ink400 }}
                    />
                  </ListItemButton>
                ))}
              </List>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5 }}>
          <Button onClick={() => setDmOpen(false)} sx={{ textTransform: 'none', fontWeight: 700, color: T.ink500 }}>
            Mbyll
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Grup i ri ── */}
      <Dialog open={groupOpen} onClose={() => setGroupOpen(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 2.5 } }}>
        <DialogTitle sx={{ fontWeight: 800, fontSize: '1rem' }}>Grup i ri</DialogTitle>
        <DialogContent dividers>
          <TextField
            autoFocus fullWidth size="small" label="Emri i grupit" required
            value={newGroup.name}
            onChange={(e) => setNewGroup((g) => ({ ...g, name: e.target.value.slice(0, 120) }))}
            helperText="E sheh vetëm kush ftohet."
            sx={{ mb: 1.5 }}
          />
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, color: T.ink500, mb: 0.5 }}>
            {newGroup.ids.length === 0
              ? 'ZGJIDH KOLEGËT'
              : `${newGroup.ids.length} ${newGroup.ids.length === 1 ? 'I ZGJEDHUR' : 'TË ZGJEDHUR'}`}
          </Typography>
          <List dense sx={{ maxHeight: 280, overflowY: 'auto' }}>
            {(people || []).length === 0 ? (
              <Typography sx={{ fontSize: '0.85rem', color: T.ink400, px: 1, py: 2 }}>
                Asnjë koleg për të shfaqur.
              </Typography>
            ) : (people || []).map((p) => {
              const picked = newGroup.ids.includes(p.id);
              return (
                <ListItemButton
                  key={p.id}
                  onClick={() => toggleGroupMember(p.id)}
                  sx={{ borderRadius: '10px' }}
                  aria-label={p.name}
                  role="checkbox"
                  aria-checked={picked}
                >
                  <ListItemAvatar sx={{ minWidth: 42 }}>
                    <Avatar sx={{
                      width: 30, height: 30, fontSize: '0.72rem', fontWeight: 800,
                      bgcolor: avatarColor(p.name), color: '#FFF',
                    }}>
                      {initial(p.name)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primaryTypographyProps={{ fontSize: '0.85rem' }}
                    secondaryTypographyProps={{ fontSize: '0.72rem' }}
                    primary={p.name}
                    secondary={p.online ? 'Online' : 'I/e gatshëm'}
                  />
                  {picked && <CheckRoundedIcon sx={{ fontSize: 18, color: T.brand }} />}
                </ListItemButton>
              );
            })}
          </List>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5 }}>
          <Button onClick={() => setGroupOpen(false)} disabled={groupBusy}
            sx={{ textTransform: 'none', fontWeight: 700, color: T.ink500 }}>
            Anulo
          </Button>
          <Button
            variant="contained" disableElevation onClick={createGroup}
            disabled={groupBusy || !newGroup.name.trim() || newGroup.ids.length === 0}
            startIcon={groupBusy ? <CircularProgress size={15} color="inherit" /> : null}
            sx={{ textTransform: 'none', fontWeight: 700, bgcolor: T.brand, '&:hover': { bgcolor: '#33788A' } }}
          >
            Krijo grupin
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Kanal i ri ── */}
      <Dialog open={channelOpen} onClose={() => setChannelOpen(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 2.5 } }}>
        <DialogTitle sx={{ fontWeight: 800, fontSize: '1rem' }}>Kanal i ri</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <TextField
              autoFocus fullWidth size="small" label="Emri i kanalit" required
              value={newChannel.name}
              onChange={(e) => setNewChannel((p) => ({ ...p, name: e.target.value }))}
              helperText="Hapësirat bëhen vija; kanali u hapet të gjithëve."
            />
            <TextField
              fullWidth size="small" label="Për çfarë është" multiline minRows={2}
              value={newChannel.purpose}
              onChange={(e) => setNewChannel((p) => ({ ...p, purpose: e.target.value }))}
              helperText="Opsionale"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5 }}>
          <Button onClick={() => setChannelOpen(false)} disabled={channelBusy}
            sx={{ textTransform: 'none', fontWeight: 700, color: T.ink500 }}>
            Anulo
          </Button>
          <Button
            variant="contained" disableElevation onClick={createChannel}
            disabled={channelBusy || !newChannel.name.trim()}
            startIcon={channelBusy ? <CircularProgress size={15} color="inherit" /> : null}
            sx={{ textTransform: 'none', fontWeight: 700, bgcolor: T.brand, '&:hover': { bgcolor: '#33788A' } }}
          >
            Krijo
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
